<template>
  <SectionWithAnchor :anchorId="AnchorLinks.LocalContent" class="row">
    <div :class="['col-12 col-md-10 col-lg-8', $style.sectionWrapper]">
      <div
        :class="[
          $style.localContentWrapper,
          {
            [$style.hg]: isPlatformHg(),
          },
        ]"
      >
        <h2 :class="$style.headingWrapper">
          {{
            $t('localLanding.localContent.sectionHeading', {
              location: routeLocationName,
            })
          }}
        </h2>
        <div
          v-for="item in localContent"
          :key="item.heading"
          :class="$style.localContentSectionWrapper"
        >
          <img
            :class="[
              $style.imageWrapper,
              {
                [$style.hg]: isPlatformHg(),
              },
            ]"
            :src="item.illustration"
            :alt="item.heading"
          />
          <div :class="$style.textWrapper">
            <h3 :class="$style.itemHeading">{{ item.heading }}</h3>
            <p :class="$style.bodyText">{{ item.body }}</p>
            <HgButton
              tag="a"
              :theme="isPlatformHg() ? 'secondary-bordered' : 'secondary'"
              :href="`${domain}${item.buttonHref}`"
              target="_blank"
              @click="onButtonClick(item.action)"
              >{{ $t('button.learnMore') }}
            </HgButton>
          </div>
        </div>
      </div>
    </div>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { getCurrentPlatform, getPlatformDomain, isPlatformHg } from '@/helpers/Platform/Platform';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks } from '@/types';
import { HgButton } from '@smg-real-estate/domus';

// Reactive data
const illustrations = ref<string[]>([]);
const platform = getCurrentPlatform();
const domain = getPlatformDomain();

// Composables
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();
const { routeGeoLocation, routeLocation } = storeToRefs(localLandingStore);

const routeLocationName = computed((): string => {
  return getLocationInfos(routeGeoLocation.value, locale.value as any).name;
});

const localContent = computed(() => {
  return [
    {
      heading: t('localLanding.localContent.shared.first.heading'),
      body: t(`localLanding.localContent.${routeLocation.value}.first.body`),
      illustration: illustrations.value[0],
      buttonHref: t(`localLanding.localContent.${routeLocation.value}.first.buttonLink`),
      action: LOCAL_LANDING_PAGE_ACTION.OPEN_TRANSFER_TAX_INFO,
    },
    {
      heading: t('localLanding.localContent.shared.second.heading'),
      body: t(`localLanding.localContent.${routeLocation.value}.second.body`),
      illustration: illustrations.value[1],
      buttonHref: t(`localLanding.localContent.${routeLocation.value}.second.buttonLink`),
      action: LOCAL_LANDING_PAGE_ACTION.OPEN_MARKET_VALUE_INFO,
    },
    {
      heading: t('localLanding.localContent.shared.third.heading'),
      body: t(`localLanding.localContent.${routeLocation.value}.third.body`),
      illustration: illustrations.value[2],
      buttonHref: t(`localLanding.localContent.${routeLocation.value}.third.buttonLink`),
      action: LOCAL_LANDING_PAGE_ACTION.OPEN_CAPITAL_GAINS_TAX_INFO,
    },
  ];
});

// Methods
const onButtonClick = (action: LOCAL_LANDING_PAGE_ACTION) => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action,
  });
};

// Lifecycle hooks
onBeforeMount(async () => {
  Promise.all([
    import(`@/assets/images/local-content/${platform}-handshake.svg?url`),
    import(`@/assets/images/local-content/${platform}-calculator.svg?url`),
    import(`@/assets/images/local-content/${platform}-rental.svg?url`),
  ]).then((images) => {
    illustrations.value = images.map((image) => image.default);
  });
});

// Expose constants and methods to template
defineExpose({
  AnchorLinks,
  isPlatformHg,
  onButtonClick,
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.sectionWrapper {
  margin-inline: auto;
}

.localContentWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-75;

  &.hg {
    gap: $space-250;

    @include grid-media-query(sm) {
      gap: $space-75;
    }
  }

  .headingWrapper {
    @include heading-2;
    @include vertical-margin;
  }

  .localContentSectionWrapper {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: $space-150;
    align-items: center;

    &.hg {
      gap: $space-200;
    }

    @include grid-media-query(sm) {
      flex-direction: row;
      justify-content: space-between;

      &:nth-child(odd) {
        flex-direction: row-reverse;
      }
    }

    .imageWrapper {
      width: calc($space-1000 + $space-400);
      padding-inline: $space-150;

      &.hg {
        width: $space-1000;
        padding-inline: $space-300;
      }
    }

    .textWrapper {
      display: flex;
      flex-direction: column;
      gap: $space-100;

      @include grid-media-query(sm) {
        align-items: flex-start;
      }

      .itemHeading {
        @include vertical-margin;
        @include heading-3;
      }

      .bodyText {
        @include body-1;
        @include vertical-margin;
      }
    }
  }
}
</style>
